import React, { useEffect, useMemo, useState } from "react";
import { api, apiError, API_BASE } from "../api/client";
import { useI18n, formatDateTime, formatDate, formatMoney } from "../i18n";
import { useAuth } from "../contexts/AuthContext";
import { PageHeader, Card, Badge, EmptyState } from "../components/shared";
import {
  Wallet, X, Search, Download, FileSpreadsheet, FileText as FileIcon,
  Banknote, CreditCard, Smartphone, User, Receipt, UserCog, CheckCircle2, AlertCircle, Archive,
} from "lucide-react";
import { toast } from "sonner";
import BulkArchiveDialog, { ArchiveRowButton, ArchiveTabs } from "../components/Archive";

const METHOD_META = {
  cash: { labelKey: "payments.cash", icon: Banknote, cls: "bg-[#2d8a56]/10 text-[#2d8a56]" },
  card: { labelKey: "payments.card", icon: CreditCard, cls: "bg-[#2563eb]/10 text-[#2563eb]" },
  wave: { labelKey: "payments.wave", icon: Smartphone, cls: "bg-[#2563eb]/10 text-[#2563eb]" },
  orange_money: { labelKey: "payments.orange_money", icon: Smartphone, cls: "bg-[#e3a03b]/15 text-[#8a5a10]" },
};

const INPUT =
  "w-full rounded-xl border border-[var(--border-default)] bg-white px-4 h-12 text-base focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none";

export default function Payments() {
  const { t } = useI18n();
  const { tenant } = useAuth();
  const cur = tenant?.currency || "XOF";

  const todayStr = formatDate(new Date());
  const [filters, setFilters] = useState({ date_from: "", date_to: "", method: "all", user_id: "all" });
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [archiveView, setArchiveView] = useState("active");
  const [archivedCount, setArchivedCount] = useState(0);
  const [bulkArchiveOpen, setBulkArchiveOpen] = useState(false);

  const fetchData = async (f = filters) => {
    setLoading(true);
    try {
      const params = {};
      if (f.date_from) params.date_from = f.date_from;
      if (f.date_to) params.date_to = f.date_to;
      if (f.method && f.method !== "all") params.method = f.method;
      if (f.user_id && f.user_id !== "all") params.user_id = f.user_id;
      if (archiveView === "archived") params.archived = "true";
      const [list, sum, arch] = await Promise.all([
        api.get("/payments", { params }),
        api.get("/payments/summary"),
        api.get("/payments", { params: { archived: "true" } }),
      ]);
      setItems(list.data);
      setSummary(sum.data);
      setArchivedCount(arch.data.length);
    } catch (err) {
      toast.error(apiError(err, t("common.error")));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [archiveView]);

  // Derived list of cashiers (from payments) to populate filter dropdown
  const cashiers = useMemo(() => {
    const map = new Map();
    items.forEach((p) => {
      if (p.cashier?.id) map.set(p.cashier.id, p.cashier);
    });
    return Array.from(map.values());
  }, [items]);

  const filteredItems = useMemo(() => {
    if (!search.trim()) return items;
    const s = search.toLowerCase();
    return items.filter((p) => {
      const pat = p.patient || {};
      return (
        (p.reference || "").toLowerCase().includes(s) ||
        (p.invoice_number || "").toLowerCase().includes(s) ||
        (pat.first_name || "").toLowerCase().includes(s) ||
        (pat.last_name || "").toLowerCase().includes(s) ||
        (pat.phone || "").toLowerCase().includes(s)
      );
    });
  }, [items, search]);

  const applyFilters = () => fetchData(filters);
  const resetFilters = () => {
    const f = { date_from: "", date_to: "", method: "all", user_id: "all" };
    setFilters(f);
    setSearch("");
    fetchData(f);
  };

  const exportFile = async (fmt) => {
    try {
      const qs = new URLSearchParams();
      if (filters.date_from) qs.set("date_from", filters.date_from);
      if (filters.date_to) qs.set("date_to", filters.date_to);
      if (filters.method && filters.method !== "all") qs.set("method", filters.method);
      if (filters.user_id && filters.user_id !== "all") qs.set("user_id", filters.user_id);
      const token = localStorage.getItem("mediflow_token");
      const res = await fetch(`${API_BASE}/payments/export/${fmt}?${qs.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `paiements_${new Date().toISOString().slice(0, 10)}.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(t("common.success"));
    } catch (err) {
      toast.error(apiError(err, t("common.error")));
    }
  };

  const openDetail = async (p) => {
    try {
      const r = await api.get(`/payments/${p.id}`);
      setDetail(r.data);
    } catch (err) {
      toast.error(apiError(err, t("common.error")));
    }
  };

  return (
    <div>
      <PageHeader
        title={t("payments.title")}
        subtitle={t("payments.count").replace("{n}", filteredItems.length)}
        actions={
          <div className="flex gap-2 flex-wrap items-center">
            <ArchiveTabs value={archiveView} onChange={setArchiveView} activeCount={archiveView === "active" ? items.length : null} archivedCount={archivedCount} />
            {archiveView === "active" && (
              <button onClick={() => setBulkArchiveOpen(true)} data-testid="bulk-archive-btn"
                className="h-12 px-4 rounded-xl border border-[var(--border-default)] bg-white hover:bg-subtle text-sm font-semibold flex items-center gap-2">
                <Archive className="w-4 h-4" /> Archiver…
              </button>
            )}
            <button
              onClick={() => exportFile("xlsx")}
              data-testid="export-xlsx-btn"
              className="h-12 px-4 rounded-xl border border-[var(--border-default)] bg-white hover:bg-subtle text-sm font-semibold flex items-center gap-2"
            >
              <FileSpreadsheet className="w-4 h-4 text-[#2d8a56]" />
              {t("payments.exportXlsx")}
            </button>
            <button
              onClick={() => exportFile("pdf")}
              data-testid="export-pdf-btn"
              className="h-12 px-4 rounded-xl border border-[var(--border-default)] bg-white hover:bg-subtle text-sm font-semibold flex items-center gap-2"
            >
              <FileIcon className="w-4 h-4 text-[#b12e31]" />
              {t("payments.exportPdf")}
            </button>
          </div>
        }
      />

      {/* Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 mb-6" data-testid="payments-summary">
        <SummaryTotal
          testid="sum-total-today"
          label={t("payments.summaryTotalToday")}
          value={formatMoney(summary?.total_today || 0, cur)}
          hint={`${summary?.count_today || 0} · ${todayStr}`}
        />
        <MethodCard
          testid="sum-cash"
          method="cash" t={t}
          amount={summary?.by_method?.cash || 0}
          count={summary?.count_by_method?.cash || 0}
          cur={cur}
        />
        <MethodCard
          testid="sum-wave"
          method="wave" t={t}
          amount={summary?.by_method?.wave || 0}
          count={summary?.count_by_method?.wave || 0}
          cur={cur}
        />
        <MethodCard
          testid="sum-orange"
          method="orange_money" t={t}
          amount={summary?.by_method?.orange_money || 0}
          count={summary?.count_by_method?.orange_money || 0}
          cur={cur}
        />
        <MethodCard
          testid="sum-card"
          method="card" t={t}
          amount={summary?.by_method?.card || 0}
          count={summary?.count_by_method?.card || 0}
          cur={cur}
        />
      </div>

      {/* Filters */}
      <Card className="p-4 mb-4">
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
          <div>
            <label className="text-xs font-semibold text-ink-muted uppercase mb-1 block">{t("payments.filterDateFrom")}</label>
            <input type="date" data-testid="filter-date-from" className={INPUT}
              value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} />
          </div>
          <div>
            <label className="text-xs font-semibold text-ink-muted uppercase mb-1 block">{t("payments.filterDateTo")}</label>
            <input type="date" data-testid="filter-date-to" className={INPUT}
              value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} />
          </div>
          <div>
            <label className="text-xs font-semibold text-ink-muted uppercase mb-1 block">{t("payments.filterMethod")}</label>
            <select data-testid="filter-method" className={INPUT}
              value={filters.method} onChange={(e) => setFilters({ ...filters, method: e.target.value })}>
              <option value="all">{t("payments.filterAll")}</option>
              <option value="cash">{t("payments.cash")}</option>
              <option value="card">{t("payments.card")}</option>
              <option value="wave">{t("payments.wave")}</option>
              <option value="orange_money">{t("payments.orange_money")}</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-ink-muted uppercase mb-1 block">{t("payments.filterUser")}</label>
            <select data-testid="filter-user" className={INPUT}
              value={filters.user_id} onChange={(e) => setFilters({ ...filters, user_id: e.target.value })}>
              <option value="all">{t("payments.filterAll")}</option>
              {cashiers.map((c) => (
                <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>
              ))}
            </select>
          </div>
          <div className="col-span-2 lg:col-span-2 flex items-end gap-2">
            <button
              onClick={applyFilters}
              data-testid="apply-filters-btn"
              className="flex-1 h-12 rounded-xl bg-brand hover:bg-brand-hover text-white font-heading font-bold"
            >
              {t("common.submit")}
            </button>
            <button
              onClick={resetFilters}
              data-testid="reset-filters-btn"
              className="h-12 px-4 rounded-xl border border-[var(--border-default)] font-semibold"
            >
              {t("payments.clearFilters")}
            </button>
          </div>
        </div>
        <div className="relative mt-3">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
          <input
            data-testid="pay-search"
            placeholder={t("patients.searchPh")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={`${INPUT} pl-10`}
          />
        </div>
      </Card>

      {/* Table / List */}
      <Card className="overflow-hidden">
        {loading ? (
          <div className="py-16 text-center text-ink-muted">{t("common.loading")}</div>
        ) : filteredItems.length === 0 ? (
          <EmptyState icon={Wallet} title={t("common.noData")} />
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-subtle text-xs uppercase tracking-wider text-ink-muted">
                    <th className="text-left py-3 px-5 font-semibold">{t("common.date")}</th>
                    <th className="text-left py-3 px-5 font-semibold">{t("payments.invoice")}</th>
                    <th className="text-left py-3 px-5 font-semibold">{t("payments.patient")}</th>
                    <th className="text-left py-3 px-5 font-semibold">{t("payments.method")}</th>
                    <th className="text-left py-3 px-5 font-semibold">{t("common.status")}</th>
                    <th className="text-left py-3 px-5 font-semibold">{t("payments.cashier")}</th>
                    <th className="text-right py-3 px-5 font-semibold">{t("payments.amount")}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredItems.map((p) => (
                    <PaymentRow key={p.id} p={p} cur={cur} t={t} onClick={() => openDetail(p)} />
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden divide-y divide-[var(--border-default)]">
              {filteredItems.map((p) => (
                <PaymentMobileCard key={p.id} p={p} cur={cur} t={t} onClick={() => openDetail(p)} />
              ))}
            </div>
          </>
        )}
      </Card>

      {detail && <DetailModal p={detail} cur={cur} t={t} onClose={() => setDetail(null)} onArchived={() => { setDetail(null); fetchData(); }} archiveView={archiveView} />}
      {bulkArchiveOpen && (
        <BulkArchiveDialog kind="payments" onClose={() => setBulkArchiveOpen(false)}
          onDone={() => { setBulkArchiveOpen(false); fetchData(); }} />
      )}
    </div>
  );
}

// ---------- Sub-components ----------

function SummaryTotal({ label, value, hint, testid }) {
  return (
    <div className="p-5 lg:col-span-1 bg-brand text-white rounded-2xl border border-brand shadow-[0_4px_16px_-4px_rgba(8,105,80,0.15)]" data-testid={testid}>
      <div className="flex items-center gap-2 mb-2">
        <Wallet className="w-5 h-5" />
        <div className="text-sm opacity-90">{label}</div>
      </div>
      <div className="stat-number text-2xl font-heading font-extrabold">{value}</div>
      {hint && <div className="text-xs opacity-80 mt-1">{hint}</div>}
    </div>
  );
}

function MethodCard({ method, amount, count, cur, t, testid }) {
  const meta = METHOD_META[method] || METHOD_META.cash;
  const Icon = meta.icon;
  return (
    <Card className="p-4 flex items-start gap-3" data-testid={testid}>
      <div className={`p-2.5 rounded-xl ${meta.cls}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-xs text-ink-muted uppercase tracking-wider">{t(meta.labelKey)}</div>
        <div className="stat-number text-lg font-heading font-bold text-ink truncate">{formatMoney(amount, cur)}</div>
        <div className="text-xs text-ink-muted">{count} op.</div>
      </div>
    </Card>
  );
}

function MethodBadge({ method, t }) {
  const meta = METHOD_META[method] || METHOD_META.cash;
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${meta.cls}`}>
      <Icon className="w-3.5 h-3.5" />
      {t(meta.labelKey)}
    </span>
  );
}

function StatusBadge({ status, t }) {
  if (status === "full") {
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-[#2d8a56]/10 text-[#2d8a56]">
        <CheckCircle2 className="w-3.5 h-3.5" /> {t("payments.statusFull")}
      </span>
    );
  }
  if (status === "partial") {
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-[#e3a03b]/15 text-[#8a5a10]">
        <AlertCircle className="w-3.5 h-3.5" /> {t("payments.statusPartial")}
      </span>
    );
  }
  // pending / failed / unknown — neutral
  return (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-subtle text-ink-muted">
      <AlertCircle className="w-3.5 h-3.5" /> {status || "—"}
    </span>
  );
}

function PaymentRow({ p, cur, t, onClick }) {
  const patient = p.patient || {};
  const cashier = p.cashier || {};
  return (
    <tr
      className="border-t border-[var(--border-default)] hover:bg-subtle/50 cursor-pointer"
      onClick={onClick}
      data-testid={`pay-${p.id}`}
    >
      <td className="py-3 px-5">
        <div className="text-sm text-ink">{formatDateTime(p.created_at)}</div>
        <div className="text-xs text-ink-muted font-mono">{p.reference}</div>
      </td>
      <td className="py-3 px-5">
        <span className="font-mono text-sm text-ink">{p.invoice_number || t("payments.noInvoice")}</span>
      </td>
      <td className="py-3 px-5">
        {patient.first_name ? (
          <div>
            <div className="font-medium text-ink">{patient.first_name} {patient.last_name}</div>
            {patient.phone && <div className="text-xs text-ink-muted">{patient.phone}</div>}
          </div>
        ) : <span className="text-ink-muted">—</span>}
      </td>
      <td className="py-3 px-5">
        <div className="flex items-center gap-1.5">
          <MethodBadge method={p.method} t={t} />
          {p.provider_data?.simulated && <Badge tone="warning">{t("payments.simulated")}</Badge>}
        </div>
      </td>
      <td className="py-3 px-5"><StatusBadge status={p.payment_status} t={t} /></td>
      <td className="py-3 px-5">
        {cashier.first_name ? (
          <span className="text-sm text-ink">{cashier.first_name} {cashier.last_name}</span>
        ) : <span className="text-ink-muted">—</span>}
      </td>
      <td className="py-3 px-5 text-right stat-number font-bold text-ink">{formatMoney(p.amount, cur)}</td>
    </tr>
  );
}

function PaymentMobileCard({ p, cur, t, onClick }) {
  const patient = p.patient || {};
  const cashier = p.cashier || {};
  return (
    <button onClick={onClick} data-testid={`pay-${p.id}`}
      className="w-full text-left p-4 hover:bg-subtle/50">
      <div className="flex items-center justify-between mb-2">
        <div className="font-mono text-xs text-ink-muted">{p.invoice_number || "—"}</div>
        <div className="stat-number font-bold text-ink">{formatMoney(p.amount, cur)}</div>
      </div>
      <div className="text-sm font-semibold text-ink">
        {patient.first_name ? `${patient.first_name} ${patient.last_name}` : "—"}
      </div>
      <div className="flex items-center gap-1.5 flex-wrap mt-2">
        <MethodBadge method={p.method} t={t} />
        <StatusBadge status={p.payment_status} t={t} />
      </div>
      <div className="mt-2 flex items-center justify-between text-xs text-ink-muted">
        <span>{formatDateTime(p.created_at)}</span>
        <span>{cashier.first_name ? `${cashier.first_name[0]}. ${cashier.last_name}` : ""}</span>
      </div>
    </button>
  );
}

function DetailModal({ p, cur, t, onClose, onArchived, archiveView }) {
  const inv = p.invoice || {};
  const patient = p.patient || {};
  const cashier = p.cashier || {};
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-6 bg-black/50" data-testid="pay-detail-modal">
      <div className="bg-white rounded-t-3xl sm:rounded-2xl w-full max-w-xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-[var(--border-default)] sticky top-0 bg-white z-10">
          <div>
            <div className="text-xs text-ink-muted uppercase tracking-wider">{t("payments.detail")}</div>
            <h3 className="font-heading font-bold text-xl font-mono">{p.reference}</h3>
          </div>
          <div className="flex items-center gap-1">
            <ArchiveRowButton kind="payments" itemId={p.id} archived={archiveView === "archived"} onDone={onArchived} />
            <button onClick={onClose} data-testid="pay-detail-close" className="p-2 rounded-lg hover:bg-subtle"><X className="w-5 h-5" /></button>
          </div>
        </div>

        <div className="p-6 space-y-5">
          {/* Big amount */}
          <div className="p-5 rounded-2xl bg-brand text-white">
            <div className="flex items-center gap-2 mb-1 opacity-90">
              <Wallet className="w-4 h-4" />
              <div className="text-xs uppercase tracking-wider">{t("payments.amount")}</div>
            </div>
            <div className="stat-number text-4xl font-heading font-extrabold">{formatMoney(p.amount, cur)}</div>
            <div className="flex items-center gap-2 mt-3">
              <MethodBadge method={p.method} t={t} />
              <StatusBadge status={p.payment_status} t={t} />
              {p.provider_data?.simulated && <Badge tone="warning">{t("payments.simulated")}</Badge>}
            </div>
            <div className="text-xs opacity-80 mt-3">{formatDateTime(p.created_at)}</div>
          </div>

          {/* Invoice block */}
          <div className="p-4 rounded-xl border border-[var(--border-default)]">
            <div className="flex items-center gap-2 mb-3">
              <Receipt className="w-4 h-4 text-brand" />
              <div className="text-sm font-semibold text-ink">{t("payments.invoice")}</div>
            </div>
            {p.invoice_number ? (
              <div className="space-y-1.5">
                <Row k={t("invoices.number")} v={<span className="font-mono">{p.invoice_number}</span>} />
                <Row k={t("common.total")} v={formatMoney(inv.total ?? p.invoice_total ?? 0, cur)} />
                <Row k={t("invoices.balance")} v={formatMoney(inv.balance ?? 0, cur)} tone={(inv.balance ?? 0) > 0 ? "danger" : "success"} strong />
              </div>
            ) : <div className="text-sm text-ink-muted">{t("payments.noInvoice")}</div>}
          </div>

          {/* Patient */}
          <div className="p-4 rounded-xl bg-subtle flex items-start gap-3">
            <User className="w-5 h-5 text-brand mt-0.5" />
            <div className="flex-1">
              <div className="text-xs text-ink-muted uppercase">{t("payments.patient")}</div>
              <div className="font-semibold text-ink">
                {patient.first_name ? `${patient.first_name} ${patient.last_name}` : "—"}
              </div>
              {patient.phone && <div className="text-sm text-ink-muted">{patient.phone}</div>}
            </div>
          </div>

          {/* Cashier */}
          <div className="p-4 rounded-xl border border-[var(--border-default)] flex items-start gap-3">
            <UserCog className="w-5 h-5 text-brand mt-0.5" />
            <div className="flex-1">
              <div className="text-xs text-ink-muted uppercase">{t("payments.cashier")}</div>
              <div className="font-semibold text-ink">
                {cashier.first_name ? `${cashier.first_name} ${cashier.last_name}` : "—"}
              </div>
              {cashier.role && <div className="text-sm text-ink-muted">{t(`roles.${cashier.role}`)}</div>}
            </div>
          </div>

          {p.reference && (
            <Row k={t("payments.reference")} v={<span className="font-mono text-sm">{p.reference}</span>} />
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ k, v, strong, tone }) {
  const toneCls = tone === "danger" ? "text-[#b12e31]" : tone === "success" ? "text-[#2d8a56]" : "text-ink";
  return (
    <div className="flex items-center justify-between">
      <span className={`text-sm ${strong ? "font-semibold text-ink" : "text-ink-muted"}`}>{k}</span>
      <span className={`${strong ? "font-bold text-base" : "text-sm"} ${toneCls}`}>{v}</span>
    </div>
  );
}
